'use client';

/**
 * FILTROS EN BARRA — la presentación alternativa del panel lateral.
 *
 * El catálogo siempre tuvo los filtros en un `<aside>` colapsable a la
 * izquierda. Esta barra dibuja LOS MISMOS filtros como una grilla de
 * desplegables encima del listado, que es como los pide la automotora que
 * viene de un sitio con ese layout.
 *
 * No reimplementa el filtrado: escribe en `useVehicleFiltersStore` con las
 * mismas claves y el mismo formato que `new-vehicle-filters` (arrays para
 * marca/modelo/transmisión/año, `priceRange` para el precio y `sortOrder` para
 * el orden). Cualquier cambio de criterio sigue viviendo en un solo lugar: si
 * mañana el panel cambia una clave, esta barra la hereda.
 *
 * Se activa con `filtersLayout="bar"`; ausente, el catálogo sigue con el panel
 * de siempre y ningún sitio publicado cambia.
 */

import React, { useMemo } from 'react';
import useVehicleFiltersStore from '@/store/useVehicleFiltersStore';

interface IdName {
  id: string | number;
  name?: string | null;
}

export interface VehicleFiltersBarProps {
  brands?: IdName[];
  models?: IdName[];
  categories?: IdName[];
  fuelTypes?: IdName[];
  transmissionCounts?: { id: string; count: number }[];
  availableYears?: (string | number)[];
  maxPrice: number;
  /** Categoría activa de la barra de tipos, para que el select la refleje. */
  selectedCategory?: string;
  onSelectCategory?: (id: string) => void;
  onClearFilters?: () => void;
  /** Colores de la sección; si no llegan, usa los del tema. */
  bgColor?: string;
  textColor?: string;
  borderColor?: string;
  accentColor?: string;
  /** Textos, ya traducidos por la sección que la monta. */
  labels?: Partial<Record<
    'tipo' | 'marca' | 'modelo' | 'anioDesde' | 'precioHasta' | 'transmision' | 'combustible' | 'ordenar' | 'todos' | 'limpiar' | 'titulo',
    string
  >>;
}

const TEXTOS_POR_DEFECTO = {
  titulo: 'Filtros de búsqueda',
  tipo: 'Tipo',
  marca: 'Marca',
  modelo: 'Modelo',
  anioDesde: 'Año desde',
  precioHasta: 'Precio hasta',
  transmision: 'Transmisión',
  combustible: 'Combustible',
  ordenar: 'Ordenar por',
  todos: 'Todos',
  limpiar: 'Limpiar filtros',
};

const ORDENES: { value: string; label: string }[] = [
  { value: 'date_desc', label: 'Más recientes' },
  { value: 'price_asc', label: 'Precio: menor a mayor' },
  { value: 'price_desc', label: 'Precio: mayor a menor' },
  { value: 'year_desc', label: 'Año: más nuevo' },
  { value: 'year_asc', label: 'Año: más antiguo' },
  { value: 'km_asc', label: 'Kilometraje: menor' },
];

/** Precios de corte, en millones, para el desplegable "precio hasta". */
const cortesDePrecio = (maxPrice: number): number[] => {
  if (!maxPrice || maxPrice <= 0) return [];
  const paso = maxPrice > 60_000_000 ? 10_000_000 : maxPrice > 25_000_000 ? 5_000_000 : 2_500_000;
  const cortes: number[] = [];
  for (let p = paso; p < maxPrice; p += paso) cortes.push(p);
  cortes.push(maxPrice);
  return cortes;
};

const formatearPrecio = (valor: number) =>
  `$${valor.toLocaleString('es-CL')}`;

const lista = (l?: IdName[]) => (Array.isArray(l) ? l.filter(Boolean) : []);

/** Lo que el store guarda como array y acá se muestra de a uno. */
const primero = (valor: unknown): string => {
  if (Array.isArray(valor)) return valor.length ? String(valor[0]) : '';
  return valor == null ? '' : String(valor);
};

export default function VehicleFiltersBar({
  brands,
  models,
  categories,
  fuelTypes,
  transmissionCounts,
  availableYears,
  maxPrice,
  selectedCategory = 'all',
  onSelectCategory,
  onClearFilters,
  bgColor,
  textColor,
  borderColor,
  accentColor,
  labels,
}: VehicleFiltersBarProps) {
  const { filters, setFilters, priceRange, setPriceRange, sortOrder, setSortOrder } =
    useVehicleFiltersStore();

  const t = { ...TEXTOS_POR_DEFECTO, ...(labels || {}) };

  const BRANDS = lista(brands);
  const MODELS = lista(models);
  const CATEGORIES = lista(categories);
  const FUELS = lista(fuelTypes);
  const TRANSMISIONES = transmissionCounts ?? [];
  const ANIOS = useMemo(
    () =>
      [...new Set((availableYears ?? []).map((a) => Number(a)).filter((a) => Number.isFinite(a)))].sort(
        (a, b) => b - a
      ),
    [availableYears]
  );
  const PRECIOS = useMemo(() => cortesDePrecio(maxPrice), [maxPrice]);

  /** Escribe una clave del store igual que el panel: array, o la saca. */
  const setUno = (clave: string, valor: string) => {
    const siguiente = { ...filters };
    if (!valor) delete (siguiente as any)[clave];
    else (siguiente as any)[clave] = [valor];
    setFilters(siguiente);
  };

  /** "Año desde": el panel guarda la lista de años del rango elegido. */
  const setAnioDesde = (desde: string) => {
    const siguiente = { ...filters };
    if (!desde) {
      delete (siguiente as any).year;
    } else {
      const minimo = Number(desde);
      (siguiente as any).year = ANIOS.filter((a) => a >= minimo).map(String);
    }
    setFilters(siguiente);
  };

  const anioDesdeActual = useMemo(() => {
    const años = (filters as any).year;
    if (!Array.isArray(años) || !años.length) return '';
    return String(Math.min(...años.map(Number).filter(Number.isFinite)));
  }, [filters]);

  const precioHastaActual = priceRange?.[1] && priceRange[1] < maxPrice ? String(priceRange[1]) : '';

  const hayFiltros =
    Object.keys(filters || {}).length > 0 ||
    !!precioHastaActual ||
    (selectedCategory && selectedCategory !== 'all');

  const estiloCampo: React.CSSProperties = {
    color: textColor || undefined,
    borderColor: borderColor || undefined,
    backgroundColor: bgColor ? 'rgba(255,255,255,0.06)' : undefined,
  };

  const Campo = ({
    etiqueta,
    value,
    onChange,
    children,
  }: {
    etiqueta: string;
    value: string;
    onChange: (v: string) => void;
    children: React.ReactNode;
  }) => (
    <label className='flex flex-col gap-1'>
      <span
        className='text-[11px] font-semibold uppercase tracking-wide opacity-60'
        style={{ color: textColor || undefined }}
      >
        {etiqueta}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className='w-full rounded-xl border bg-white px-3 py-2.5 text-sm outline-none transition
                   sm:px-4 sm:py-3.5 sm:text-base
                   focus:ring-2 focus:ring-offset-0 dark:bg-dark-bg dark:border-dark-border'
        style={estiloCampo}
      >
        {children}
      </select>
    </label>
  );

  return (
    <div
      className='rounded-2xl border p-4 sm:p-5'
      style={{
        backgroundColor: bgColor || undefined,
        borderColor: borderColor || undefined,
      }}
    >
      <div className='mb-3 flex items-center justify-between gap-3'>
        <h3
          className='text-sm font-bold uppercase tracking-wide'
          style={{ color: textColor || undefined }}
        >
          {t.titulo}
        </h3>
        {hayFiltros && (
          <button
            type='button'
            onClick={onClearFilters}
            className='text-xs font-semibold underline-offset-2 hover:underline'
            style={{ color: accentColor || undefined }}
          >
            {t.limpiar}
          </button>
        )}
      </div>

      {/* Dos columnas ya en el celular: apilados de a uno, los desplegables
          ocupaban más alto que el resto de la home. De `sm` en adelante no
          cambia nada. */}
      <div className='grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-3'>
        {CATEGORIES.length > 0 && (
          <Campo
            etiqueta={t.tipo}
            value={selectedCategory === 'all' ? '' : selectedCategory}
            onChange={(v) => onSelectCategory?.(v || 'all')}
          >
            <option value=''>{t.todos}</option>
            {CATEGORIES.map((c) => (
              <option key={String(c.id)} value={String(c.id)}>
                {c.name ?? String(c.id)}
              </option>
            ))}
          </Campo>
        )}

        <Campo etiqueta={t.marca} value={primero((filters as any).brand)} onChange={(v) => setUno('brand', v)}>
          <option value=''>{t.todos}</option>
          {BRANDS.map((b) => (
            <option key={String(b.id)} value={String(b.id)}>
              {b.name ?? String(b.id)}
            </option>
          ))}
        </Campo>

        <Campo etiqueta={t.modelo} value={primero((filters as any).model)} onChange={(v) => setUno('model', v)}>
          <option value=''>{t.todos}</option>
          {MODELS.map((m) => (
            <option key={String(m.id)} value={String(m.id)}>
              {m.name ?? String(m.id)}
            </option>
          ))}
        </Campo>

        <Campo etiqueta={t.anioDesde} value={anioDesdeActual} onChange={setAnioDesde}>
          <option value=''>{t.todos}</option>
          {ANIOS.map((a) => (
            <option key={a} value={String(a)}>
              {a}
            </option>
          ))}
        </Campo>

        <Campo
          etiqueta={t.precioHasta}
          value={precioHastaActual}
          onChange={(v) => setPriceRange([0, v ? Number(v) : maxPrice])}
        >
          <option value=''>{t.todos}</option>
          {PRECIOS.map((p) => (
            <option key={p} value={String(p)}>
              {formatearPrecio(p)}
            </option>
          ))}
        </Campo>

        <Campo
          etiqueta={t.transmision}
          value={primero((filters as any).transmission)}
          onChange={(v) => setUno('transmission', v)}
        >
          <option value=''>{t.todos}</option>
          {TRANSMISIONES.map((tr) => (
            <option key={tr.id} value={tr.id}>
              {tr.id}
            </option>
          ))}
        </Campo>

        <Campo
          etiqueta={t.combustible}
          value={primero((filters as any).fuel_type)}
          onChange={(v) => setUno('fuel_type', v)}
        >
          <option value=''>{t.todos}</option>
          {FUELS.map((f) => (
            <option key={String(f.id)} value={String(f.id)}>
              {f.name ?? String(f.id)}
            </option>
          ))}
        </Campo>

        <Campo etiqueta={t.ordenar} value={sortOrder || ''} onChange={(v) => setSortOrder(v)}>
          {ORDENES.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Campo>
      </div>
    </div>
  );
}
